import { textwrap } from 'd3-textwrap';
import { isEmpty } from 'lodash';
import { wrapTextToLines } from './htmlUtil';
import { STYLES } from '../../../Constants';
import Filters from './Filters';


export default class SankeyUtil {

    static TEXT_PADDING = 6;

    static HIGHLIGHT_MODE = {
        COLOR: 'color',
        BLACK_AND_WHITE: 'bw'
    };

    selectedElement = null;

    constructor(d3, sankey, svg){
        this.d3 = d3;
        this.sankey = sankey;
        /* Custom method based on sankey.link = function() to 
        move links start and end pints to the middle of node x value */
        sankey.linkGenerator = function() {
            var curvature = .5;

            function link(d) {
            var x0 = d.source.x + d.source.dx/2,
                x1 = d.target.x + d.target.dx/2,
                xi = d3.interpolateNumber(x0, x1),
                x2 = xi(curvature),
                x3 = xi(1 - curvature),
                y0 = d.source.y + d.sy + d.dy / 2,
                y1 = d.target.y + d.ty + d.dy / 2;
            return "M" + x0 + "," + y0
                + "C" + x2 + "," + y0
                + " " + x3 + "," + y1
                + " " + x1 + "," + y1;
            }

            link.curvature = function(_) {
            if (!arguments.length) return curvature;
            curvature = +_;
            return link;
            };

            return link;
        };

        this.svg = svg;
    }

    buildFiltersByCategory(nodes, categories, canvasWidth, padding, elementID){

        // clear before
        document.getElementById(elementID).innerHTML = '';

        for(const category of categories){
            const categoryNodes = nodes.filter(n=>n.category.name === category.name);

            const filter = this.buildFilterByCategory(categoryNodes, category);
            const filterWidth = canvasWidth * category.weight - padding.left - padding.right;
            filter.style.width = filterWidth + 'px';
            filter.style.marginLeft = padding.left + 'px';
            filter.style.marginRight = padding.right + 'px';

            document.getElementById(elementID).appendChild(filter);
        }

    }

    buildFilterByCategory(nodes, category){

        const select = document.createElement('select');
        select.classList.add('dd-filter');

        const emptyOption = document.createElement('option');
            emptyOption.value = -1;
            emptyOption.text = 'Todos';
            select.appendChild(emptyOption);

        for(const node of nodes){
            const option = document.createElement('option');
            option.value = node.id;
            option.text = node.name;
            select.appendChild(option);
        }
        select.addEventListener('change', (event) => {
            const selectedNodeId = event.target.value;

            if(selectedNodeId == -1){
                this.showNodesOfCategory(category);
            }else{
                this.hideNodesOfCategory(category);
                this.showNodesOfCategory(category, [selectedNodeId]);
            }
        });

        return select;
    }

    drawCategoriesLayer(graphRecord, width, height, padding, iconDrag = false){

        const me = this;
        const categories =  graphRecord.template.groups;

        const CATEGORIES_LAYER = 'categories-layer';

        // clear before adding the layer
        this.d3.selectAll(`.${CATEGORIES_LAYER}`).remove();

        // create layer
        const categoriesLayer = this.svg.append('g')
            .attr('class', CATEGORIES_LAYER)

        const dashedLinesLayer = categoriesLayer.append('g')
            .attr('class', 'dashed-lines');

        let x = 0;

        for(let index=0; index < categories.length-1; index++){

            const category = categories[index];

            const categoryWidth = category.weight * width;
            x = x + categoryWidth;
            dashedLinesLayer.append("line")
                .attr("x1", x)
                .attr("y1", 0)
                .attr("x2", x)
                .attr("y2", height)
                .style("stroke", "black")
                .style("stroke-width", 1)
                .style("stroke-dasharray", "5, 5"); // Defines the pattern of dashes and gaps

            if(category.subgroup){
                dashedLinesLayer.append("line")
                    .attr("x1", x-categoryWidth)
                    .attr("y1", height/2)
                    .attr("x2", x)
                    .attr("y2", height/2)
                    .style("stroke", "black")
                    .style("stroke-width", 1)
                    .style("stroke-dasharray", "5, 5"); // Defines the pattern of dashes and gaps
            }
        }

        const topLegendsLayer = categoriesLayer.append('g')
            .attr('class', 'category-labels');
        const bottomLegendsLayer = categoriesLayer.append('g')
            .attr('class', 'category-labels');


        x = 0;
        const y = 0;
        const iconSize = 32;
        const gap = 5;

        // category label layout 
        // | paddingLeft | Icon | gap | text | paddingRight |

        categories.forEach((category, index)=>{
            const categoryWidth = categories[index].weight * width;
            const textWidth = categoryWidth - padding.left - iconSize - gap - padding.right;
            const iconWidth = iconSize;

            const labelConfig = {
                x,
                y,
                padding,
                textWidth,
                iconWidth,
                iconSize,
                gap,
                draggable: iconDrag
            };

            if(category.subgroup){
                // Top subgroup - icon and text
                me.renderCategoryLabel({
                    ...labelConfig,
                    layer: topLegendsLayer,
                    category: category.subgroup[0],
                    textClass: "top-text"
                });

                // Bottom subgroup - icon and text
                me.renderCategoryLabel({
                    ...labelConfig,
                    layer: bottomLegendsLayer,
                    category: category.subgroup[1]
                });
            } else {
                // Single category - icon and text
                me.renderCategoryLabel({
                    ...labelConfig,
                    layer: bottomLegendsLayer,
                    category: category
                });
            }

            x = x + categoryWidth;
        });

        // by calling textwrap() the texts tag is transformed into foreignObject
        // Some calculations need to be adjusted

        // get max height used by the labels
        const categoryElements = this.d3.selectAll(`.${CATEGORIES_LAYER} foreignObject div`).nodes();
        const maxHeight = this.d3.max(categoryElements, (c)=>c.offsetHeight);
        
        // get the max heigh layer and resize parent objects
        this.d3.selectAll(`.${CATEGORIES_LAYER} foreignObject`)
            .attr('class', 'category-label')
            //.attr('pointer-events', 'none')
            .attr('height', maxHeight)
            .style('font-size', '0.8em')
            .style('cursor', 'default')
            .on("click", function (d, index, paths) {
                me.d3.event.stopPropagation();
                const labelElement = me.d3.select(this);
                const labelBox = me.d3.select(this.parentNode);
                const definition = labelBox.attr('definition');
                if(definition){
                    me.setMoreDetails(definition);
                }else{
                    me.setMoreDetails(null);
                }
                if(!me.areSelectionsEqual(labelElement, me.selectedElement)){
                    me.setSelectedElement(labelElement);
                }
            });
            // .on('mouseover', function (d, index, paths) {
            //     const labelBox = me.d3.select(this.parentNode);
            //     const definition = labelBox.attr('definition')
            //     if(definition){
            //         me.setMoreDetails(definition);
            //     }
            // })
            // .on("mouseout", function (d, index, paths) {
            //     me.setMoreDetails(null);
            // });

        // move top layer to the top
        topLegendsLayer.attr("transform", "translate(" + 0 + "," + -18 + ")");
           
        // locate labels to the bottom
        const newY = height - maxHeight - padding.bottom;
        bottomLegendsLayer.attr("transform", "translate(" + 0 + "," + newY + ")");
        return maxHeight;
    }

    addColorCircle(text, color){
        return `<div class="legend-circle" style="background: ${color}"></div><span>${text}<span>`;
    }

    renderCategoryLabel(config) {
        const { layer, category, x, y, padding, textWidth, iconWidth, iconSize, gap, draggable } = config;
        const me = this;

        const labelBox = layer.append('g')
            .attr('definition', category.definition);

        // Add text with color circle
        labelBox.append("text")
            .attr("class", config.textClass || "")
            //.text(me.addColorCircle(category.label, category.colors[0]))
            .text(category.label)
            .attr('x', x + padding.left + iconWidth + gap)
            .attr('y', y + padding.top)
            .call(textwrap().bounds({ width: textWidth, height: 500 }));

        // Add icon if exists (located after text despite in the layout is the opposite. - Layering order rules)
        const iconSource = category.icon;
        if (iconSource) {

            let iconX = x + padding.left;
            let iconY = y + padding.top + 20;

            // override if details exists
            if(category.labelDetail){
                if(category.labelDetail.x || category.labelDetail.x == 0){
                    iconX = category.labelDetail.x;
                }
                if(category.labelDetail.y || category.labelDetail.y == 0){
                    iconY = category.labelDetail.y;
                }
            }

            me.addIconToLayer({
                layer: labelBox,
                iconSource: '/icons/' + iconSource,
                color: category.colors[0],
                x: iconX,
                y: iconY,
                iconSize: iconSize,
                draggable: draggable,
                categoryName: category.name
            });
        }

        return labelBox;
    }

    addIconToLayer(props) {
        const {layer, iconSource, color, x, y, iconSize, draggable, categoryName} = props;
        const me = this;

        if (!iconSource) return;

        // Check if iconSource is a URL or a path (starts with http://, https://, or /)
        const isExternalResource = iconSource.startsWith('http://') ||
                                   iconSource.startsWith('https://') ||
                                   iconSource.startsWith('/');

        if (isExternalResource) {
            // Use SVG <image> element to load from URL or public folder
            const icon = layer.append('image')
                .attr('class', 'category-icon')
                .attr('href', iconSource)
                .attr('x', x)
                .attr('y', y)
                .attr('width', iconSize)
                .attr('height', iconSize)
                .attr('data-category', categoryName)
                //.style('filter', this.getColorFilter(color));

            // Make icon draggable only if draggable is enabled
            if (draggable) {
                icon.style('cursor', 'move');
                icon.call(me.d3.drag()
                    .on('drag', function() {
                        me.d3.select(this)
                            .attr('x', me.d3.event.x - iconSize / 2)
                            .attr('y', me.d3.event.y - iconSize / 2);
                    })
                );
            }
        }
    }

    // Generates a CSS filter to colorize an image to a specific color
    // Note: This is an approximation and works best with monochrome icons
    getColorFilter(hexColor) {
        // Remove # if present
        const color = hexColor.replace('#', '');

        // Convert hex to RGB
        const r = parseInt(color.substr(0, 2), 16) / 255;
        const g = parseInt(color.substr(2, 2), 16) / 255;
        const b = parseInt(color.substr(4, 2), 16) / 255;

        // Create a filter that colorizes the image
        // This uses a combination of grayscale, sepia, and hue-rotate
        // Note: For better color matching, consider using a server-side solution
        // or inline SVG with currentColor
        return `brightness(0) saturate(100%) invert(${Math.round(((r + g + b) / 3) * 100)}%) sepia(100%) saturate(10000%) hue-rotate(${this.rgbToHue(r, g, b)}deg) brightness(${Math.max(r, g, b) * 100}%)`;
    }

    // Helper function to calculate hue from RGB
    rgbToHue(r, g, b) {
        const max = Math.max(r, g, b);
        const min = Math.min(r, g, b);
        const delta = max - min;

        if (delta === 0) return 0;

        let hue;
        if (max === r) {
            hue = ((g - b) / delta) % 6;
        } else if (max === g) {
            hue = (b - r) / delta + 2;
        } else {
            hue = (r - g) / delta + 4;
        }

        hue = Math.round(hue * 60);
        if (hue < 0) hue += 360;

        return hue;
    }

    onCheckboxChange (event){
        const checkbox = event.target;
        if (checkbox.checked){
            this.showNodesOfCategory(checkbox.name);
        }else{
            this.hideNodesOfCategory(checkbox.name);
        }
    }

    showNodesOfCategory(category, nodeIdsToShow){
        let nodesOfCategory = this.d3.selectAll(`.node[category="${category.name}"]`);
        if(nodeIdsToShow && Array.isArray(nodeIdsToShow) && nodeIdsToShow.length>0){
            nodesOfCategory = nodesOfCategory.filter(n => nodeIdsToShow.indexOf(n.id) !== -1)
        }

        // nodes
        nodesOfCategory.style('display', '');

        // links
        nodesOfCategory.each((node)=>{
            const sourceLinksToShow = this.d3.selectAll('.link')
            .filter(l=>{ // filter by source
                return l.source.id === node.id;
            })
            .filter(l=>{ // filter by "the target related node is visible"
                const targetNode = this.d3.select(`.node[id="${l.target.id}"]`);
                return targetNode.style('display') !== 'none';
            });
            sourceLinksToShow.style('display', '');

            const targetLinksToShow = this.d3.selectAll('.link')
            .filter(l=>{ // filter by target
                return l.target.id === node.id;
            })
            .filter(l=>{ // filter by "the source related node is visible"
                const sourceNode = this.d3.select(`.node[id="${l.source.id}"]`);
                return sourceNode.style('display') !== 'none';
            });
            targetLinksToShow.style('display', '');
        });
    }

    hideNodesOfCategory(category){
        // nodes
        const nodesOfCategory = this.d3.selectAll(`.node[category="${category.name}"]`);
        nodesOfCategory.style('display', 'none');

        // links
        nodesOfCategory.each((node)=>{
            const associatedLinks = this.d3.selectAll('.link').filter(l=>{
                return l.source.id === node.id || l.target.id === node.id;
            });
            associatedLinks.style('display', 'none');
        });
    }

    drawGraph (graphData, canvasWidth, canvasHeight, nodeDraggingIsEnabled, nodeTextAnchorChangeEnabled, highlightMode=SankeyUtil.HIGHLIGHT_MODE.COLOR, filter={}) {
        const me = this;

        this.sankey
            .nodes(graphData.nodes)
            .links(graphData.links)
            .nodeHeightScaleFactor(0.7) // use this value to scale the entire graph in vertical
            .layout(1);

        // tooltips
        const linkTip = this.d3.tip()
            .html(d => {
                return `
                    <div class="graph-tooltip">
                        <strong>Desde:</strong> ${d.source.name}<br>
                        <strong>Hacia:</strong> ${d.target.name}<br>
                    <div>
                `;
            })
            .direction('nw');

        const nodeTip = this.d3.tip()
            .html(d => {
                if(d.tooltip){
                    return `
                        <div class="graph-tooltip">
                            ${d.tooltip}
                        <div>
                    `;
                }else{
                    return null;
                }
            })
            .direction('nw');;

        this.svg.on("mouseleave", function(d, index, canvas){
            //me.restoreStylesOnAllElements();
        });
        // clear details click on other places
        this.svg.on('click', function(d, index, canvas){
            me.setMoreDetails(null);
            me.setSelectedElement(null);
        });
    
        // add in the links
        const linkPaths = this.svg.append("g")
            .attr('class', 'links-layer')
            .selectAll(".link")
            .data(graphData.links)
            .enter()
            .append("path")
                .attr("class", "link")
                .attr("d", this.sankey.linkGenerator())
                .attr("id", d => `${d.source.id}-to-${d.target.id}`)
                .attr("source-node-id", d=>d.source.id)
                .attr("target-node-id", d=>d.target.id)
                .style("stroke-width", function (d) { return Math.max(1, d.dy); })
                .style("stroke", d=>d.color)
                .call(linkTip);
        
        // hover events
        linkPaths.on("mouseover", function (d, index, paths) {
            const newX = me.d3.event.x;
            const newY = me.d3.event.y;
            linkTip.show(d, this);
            linkTip.relocate(newX, newY);
                
            if(highlightMode === SankeyUtil.HIGHLIGHT_MODE.COLOR){
                // no nothing
            }else if(highlightMode === SankeyUtil.HIGHLIGHT_MODE.BLACK_AND_WHITE){
                me.grayAllLinks();
                me.grayAllNodes();
            }
            // highlight link and linked nodes

            //link
            const linkElement = me.d3.select(this);
            if(!me.areSelectionsEqual(linkElement, me.selectedElement)){
                linkElement.style("stroke", STYLES.HIGHLIGHT.PATH_COLOR)
                    .style("stroke-opacity", STYLES.HIGHLIGHT.PATH_OPACITY);
            }

            // source node
            const sourceNodeElement = me.d3.select('#'+d.source.id);
            const sourceNodeRectElement = sourceNodeElement.select('rect');
            if(!me.areSelectionsEqual(sourceNodeRectElement, me.selectedElement)){
                me.updateNode({
                    id: d.source.id,
                    nodeColor: STYLES.HIGHLIGHT.FILL_COLOR,
                    fontColor: STYLES.HIGHLIGHT.FONT_COLOR,
                    fontSize: STYLES.HIGHLIGHT.FONT_SIZE,
                    fontWeight: STYLES.HIGHLIGHT.FONT_WEIGHT
                });
            }
    
            // target node
            const targetNodeElement = me.d3.select('#'+d.target.id);
            const targetNodeRectElement = targetNodeElement.select('rect');
            if(!me.areSelectionsEqual(targetNodeRectElement, me.selectedElement)){
                me.updateNode({
                    id: d.target.id,
                    nodeColor: STYLES.HIGHLIGHT.FILL_COLOR,
                    fontColor: STYLES.HIGHLIGHT.FONT_COLOR,
                    fontSize: STYLES.HIGHLIGHT.FONT_SIZE,
                    fontWeight: STYLES.HIGHLIGHT.FONT_WEIGHT
                });
            }
            

            // aggregations / addons
            // if(d.definition){
            //     me.setMoreDetails(d.definition);
            // }
        });

        linkPaths.on("mouseout", function (d, index, paths) {
            linkTip.hide(d, this);
            if(highlightMode === SankeyUtil.HIGHLIGHT_MODE.COLOR){
                //link
                const linkElement = me.d3.select(this)
                if(!me.areSelectionsEqual(linkElement, me.selectedElement)){
                    linkElement.style("stroke", d.color)
                        .style("stroke-opacity", STYLES.NORMAL.PATH_OPACITY);
                }
        
                //connected nodes (restore to originals)
                // source
                const sourceNodeElement = me.d3.select('#'+d.source.id);
                const sourceNodeRectElement = sourceNodeElement.select('rect');
                if(!me.areSelectionsEqual(sourceNodeRectElement, me.selectedElement)){
                    me.updateNode({
                        id: d.source.id,
                        nodeColor: d.source.color,
                        fontColor: STYLES.NORMAL.FONT_COLOR,
                        fontSize: STYLES.NORMAL.FONT_SIZE,
                        fontWeight: STYLES.NORMAL.FONT_WEIGHT
                    });
                }
        
                // target
                const targetNodeElement = me.d3.select('#'+d.target.id);
                const targetNodeRectElement = targetNodeElement.select('rect');
                if(!me.areSelectionsEqual(targetNodeRectElement, me.selectedElement)){
                    me.updateNode({
                        id: d.target.id,
                        nodeColor: d.target.color,
                        fontColor: STYLES.NORMAL.FONT_COLOR,
                        fontSize: STYLES.NORMAL.FONT_SIZE,
                        fontWeight: STYLES.NORMAL.FONT_WEIGHT
                    });
                }
            }else if(highlightMode === SankeyUtil.HIGHLIGHT_MODE.BLACK_AND_WHITE){
                me.grayAllLinks();
                me.grayAllNodes();
            }
            //me.setMoreDetails(null);
        });
        
        linkPaths.on("click", function (d, index, paths) {
            me.d3.event.stopPropagation();
            const linkElement = me.d3.select(this);
            if(d.definition){
                me.setMoreDetails(d.definition);
            }else{
                me.setMoreDetails(null);
            }
            if(!me.areSelectionsEqual(linkElement, me.selectedElement)){
                me.setSelectedElement(linkElement);
            }
        })
    
        // add in the nodes
        var nodes = this.svg.append("g")
            .attr('class', 'nodes-layer')
            .selectAll(".node")
            .data(graphData.nodes)
            .enter()
            .append("g")
                .attr("class", "node")
                .attr("id", d => d.id)
                .attr("category", d => d.category.name)
                .call(nodeTip)
                .attr("transform", function (d) { return "translate(" + d.x + "," + d.y + ")"; });
    
        // add the rectangles for the nodes
        nodes.append("rect")
            .attr("class", "node-rect")
            .attr("height", function (d) { return d.dy; })
            .attr("width", this.sankey.nodeWidth())
            .style("fill", d=>d.color)
            .attr("stroke", STYLES.NORMAL.STROKE_COLOR)
            //.style("stroke", function (d) { return d3.rgb(d.color).darker(2); })
            .attr("stroke-width", "0")
            .attr("rx", d=> d.dy > this.sankey.nodeWidth() ? this.sankey.nodeWidth()/2 : 5)
            .attr("ry", d=> d.dy > this.sankey.nodeWidth() ? this.sankey.nodeWidth()/2 : 5)
            .text(function (d) { return d.name });
    
        // add in the title for the nodes
        const nodeTexts = nodes
            .append("text")
            .attr("class", "node-text")
            .attr('pointer-events', 'none') // disable hover events on these texts
            .attr("x", -6)
            .attr("y", function (d) { 
                // get lines height and subtract half of that size to center
                const lines = wrapTextToLines(d.shortName, 100, STYLES.NORMAL.FONT_SIZE+'px Arial');
                const lineHeight = 1.2;
                const totalLinesHight = STYLES.NORMAL.FONT_SIZE * lineHeight * lines.length;
                return d.dy / 2 - totalLinesHight / 2; 
            })
            .attr("dy", ".35em")
            .attr("text-anchor", "end")
            .attr("transform", null)
            .selectAll("tspan")
                .data(function (d) { return wrapTextToLines(d.shortName, 100, STYLES.NORMAL.FONT_SIZE+'px Arial'); })
                .enter()
                .append("tspan")
                .attr("x", 20)
                .attr("dy", "1.2em")
                .text(function (d) { return d; })
            .style("font-size", STYLES.NORMAL.FONT_SIZE);

        // relocate texts
        this.relocateTextsOfNodes(nodes, canvasWidth);

        nodes.on('mouseover', function(d, index, nodes){
            const newX = me.d3.event.x;
            const newY = me.d3.event.y;
            nodeTip.show(d, this);
            nodeTip.relocate(newX, newY);

            if(highlightMode === SankeyUtil.HIGHLIGHT_MODE.COLOR){
                // do nothing
            }else if(highlightMode === SankeyUtil.HIGHLIGHT_MODE.BLACK_AND_WHITE){
                // gray all links and nodes
                me.grayAllLinks();
                me.grayAllNodes();
            }
            // current node
            const nodeElement = me.d3.select(this);
            const nodeRectElement = nodeElement.select('rect');
            if(!me.areSelectionsEqual(nodeRectElement, me.selectedElement)){
                me.updateNode({
                    id: d.id,
                    nodeColor: STYLES.HIGHLIGHT.FILL_COLOR,
                    fontColor: STYLES.HIGHLIGHT.FONT_COLOR,
                    fontSize: STYLES.HIGHLIGHT.FONT_SIZE,
                    fontWeight: STYLES.HIGHLIGHT.FONT_WEIGHT
                });
            }

            // linked nodes
            const linkedNodeIds = me.getLinkedNodeIds(d);

            for(const nodeId of linkedNodeIds){     
                
                const linkedNodeElement = me.d3.select('#'+nodeId);
                const linkedNodeRectElement = linkedNodeElement.select('rect');
                if(!me.areSelectionsEqual(linkedNodeRectElement, me.selectedElement)){
                    me.updateNode({
                        id: nodeId,
                        nodeColor: STYLES.HIGHLIGHT.FILL_COLOR,
                        fontColor: STYLES.HIGHLIGHT.FONT_COLOR,
                        fontSize: STYLES.HIGHLIGHT.FONT_SIZE,
                        fontWeight: STYLES.HIGHLIGHT.FONT_WEIGHT
                    });
                }
            }

            // "linked" links
            d.sourceLinks.forEach(sl => {
                const linkedLinkElement = me.d3.select('#'+sl.source.id+"-to-"+sl.target.id);
                if(!me.areSelectionsEqual(linkedLinkElement, me.selectedElement)){
                    me.updateLink({
                        sourceNodeId: sl.source.id,
                        targetNodeId: sl.target.id,
                        pathColor: STYLES.HIGHLIGHT.PATH_COLOR,
                        pathOpacity: STYLES.HIGHLIGHT.PATH_OPACITY
                    });
                }
            });
            d.targetLinks.forEach(tl => {
                const linkedLinkElement = me.d3.select('#'+tl.source.id+"-to-"+tl.target.id);
                if(!me.areSelectionsEqual(linkedLinkElement, me.selectedElement)){
                    me.updateLink({
                        sourceNodeId: tl.source.id,
                        targetNodeId: tl.target.id,
                        pathColor: STYLES.HIGHLIGHT.PATH_COLOR,
                        pathOpacity: STYLES.HIGHLIGHT.PATH_OPACITY
                    });
                }
            });

            // aggregations / addons
            // if(d.definition){
            //     me.setMoreDetails(d.definition);
            // }

        });
        nodes.on('mouseout', function(d, index, nodes){
            nodeTip.hide(d, this);
            if(highlightMode === SankeyUtil.HIGHLIGHT_MODE.COLOR){

                const nodeElement = me.d3.select(this);
                const nodeRectElement = nodeElement.select('rect');

                if(!me.areSelectionsEqual(nodeRectElement, me.selectedElement)){
                    me.updateNode({
                        id: d.id,
                        nodeColor: d.color,
                        fontColor: STYLES.NORMAL.FONT_COLOR,
                        fontSize: STYLES.NORMAL.FONT_SIZE,
                        fontWeight: STYLES.NORMAL.FONT_WEIGHT
                    });
                }


                // linked nodes
                const linkedNodeIds = me.getLinkedNodeIds(d);

                for(const nodeId of linkedNodeIds){
                    
                    const nodeData = me.sankey.nodes().find(n=>n.id===nodeId);

                    const linkedNodeElement = me.d3.select('#'+nodeId);
                    const linkedNodeRectElement = linkedNodeElement.select('rect');
                    if(!me.areSelectionsEqual(linkedNodeRectElement, me.selectedElement)){
                        me.updateNode({
                            id: nodeId,
                            nodeColor: nodeData.color,
                            fontColor: STYLES.NORMAL.FONT_COLOR,
                            fontSize: STYLES.NORMAL.FONT_SIZE,
                            fontWeight: STYLES.NORMAL.FONT_WEIGHT
                        });
                    }
                }

                // "linked" links
                d.sourceLinks.forEach(sl => {
                    const linkedLinkElement = me.d3.select('#'+sl.source.id+"-to-"+sl.target.id);
                    if(!me.areSelectionsEqual(linkedLinkElement, me.selectedElement)){
                        me.updateLink({
                            sourceNodeId: sl.source.id,
                            targetNodeId: sl.target.id,
                            pathColor: sl.color,
                            pathOpacity: STYLES.NORMAL.PATH_OPACITY
                        });
                    }
                });
                d.targetLinks.forEach(tl => {
                    const linkedLinkElement = me.d3.select('#'+tl.source.id+"-to-"+tl.target.id);
                    if(!me.areSelectionsEqual(linkedLinkElement, me.selectedElement)){
                        me.updateLink({
                            sourceNodeId: tl.source.id,
                            targetNodeId: tl.target.id,
                            pathColor: tl.color,
                            pathOpacity: STYLES.NORMAL.PATH_OPACITY
                        });
                    }
                })
            }else if(highlightMode === SankeyUtil.HIGHLIGHT_MODE.BLACK_AND_WHITE){
                // gray all links and nodes
                me.grayAllLinks();
                me.grayAllNodes();
            }

            // aggregations / addons
            //me.setMoreDetails(null);

        });

        if(nodeTextAnchorChangeEnabled){
            nodes.on('contextmenu', function(d, index, nodes){
                me.d3.event.preventDefault();
                const node = me.d3.select(this);
                const text = node.select('text');
                me.changeTextDirection(text);
            });
        }

        if(nodeDraggingIsEnabled){
            nodes.call(me.d3.drag()
                .subject(function (d) { return d; })
                .on("start", function (d, index, nodes) { 

                    const nodeElement = me.d3.select(this);
                    const nodeRectElement = nodeElement.select('rect');

                    // This is the place to get the on('click') event
                    if(d.definition){
                        me.setMoreDetails(d.definition);
                    }else{
                        me.setMoreDetails(null);
                    }

                    if(!me.areSelectionsEqual(nodeRectElement, me.selectedElement)){
                        me.setSelectedElement(nodeRectElement);
                    }
                    
                    this.parentNode.appendChild(this); 
                })
                .on("drag", dragmove));
        }

        // apply filters if any
        if(!isEmpty(filter)){
            me.doFiltering(filter, linkPaths, nodes);
        }
    
        me.relocateNodes(linkPaths, canvasWidth); 
    
        // the function for moving the nodes
        function dragmove(d) {
            const node = me.d3.select(this)
                .attr("transform",
                    "translate("
                    //+ d.x + ","
                    + (d.x = Math.max(
                        0, Math.min(canvasWidth - d.dx, me.d3.event.x))
                    ) + ","
                    + (d.y = Math.max(
                        0, Math.min(canvasHeight - d.dy, me.d3.event.y))
                    ) + 
                    ")"
                );
            me.sankey.relayout();
            linkPaths.attr("d", me.sankey.linkGenerator());

        }
    };

    relocateTextsOfNodes(nodes, canvasWidth){

        const me = this;
        nodes.each(function(d, index, array){
            const node = me.d3.select(this);
            const text = node.select('text');
            if(d.textAnchor === 'end'){     // aligned to the left
                text
                .attr('x',  -SankeyUtil.TEXT_PADDING)
                .attr("text-anchor", "end")
                .selectAll("tspan")
                    .attr("x",  -SankeyUtil.TEXT_PADDING);
            }else{                          // aligned to the right
                text
                    .attr('x', SankeyUtil.TEXT_PADDING + me.sankey.nodeWidth())
                    .attr("text-anchor", "start")
                    .selectAll("tspan")
                        .attr("x", SankeyUtil.TEXT_PADDING + me.sankey.nodeWidth());
            }
        });
    }

    updateNode(nodeData){
        if(nodeData && nodeData.id){

            const rect = this.d3.select(`.node[id="${nodeData.id}"] rect`);
            const text = this.d3.select(`.node[id="${nodeData.id}"] text`);

            if(rect){
                if(nodeData.nodeColor){
                    rect.style("fill", nodeData.nodeColor); // change fill color
                }
            }
            if(text){
                if(nodeData.fontColor){
                    text.attr("fill", nodeData.fontColor);
                }
                if(nodeData.fontSize){
                    text.style("font-size", `${nodeData.fontSize}px`);
                }
                if(nodeData.fontWeight){
                    text.style('font-weight', nodeData.fontWeight);
                }
            }
        }
    }

    updateLink(linkData) {
        if(linkData && linkData.sourceNodeId && linkData.targetNodeId){
            //const path = this.d3.select(this);
            const path = this.d3.select(`path.link[source-node-id=${linkData.sourceNodeId}][target-node-id=${linkData.targetNodeId}]`)
            if(!isEmpty(path.data())){
                if(linkData.pathColor){
                    path.style("stroke", linkData.pathColor)
                }
                if(linkData.pathOpacity || linkData.pathOpacity===null){
                    path.style("stroke-opacity", linkData.pathOpacity);
                }
            }
        }
    }

    getLinkedNodeIds (d) {
        const linkedNodeIds = [];

        const sourceLinks = d.sourceLinks;
        for(const sl of sourceLinks){
            linkedNodeIds.push(sl.target.id);
        }
        const targetLinks = d.targetLinks;
        for(const sl of targetLinks){
            linkedNodeIds.push(sl.source.id);
        }

        return linkedNodeIds;
    } 

    getNodesDetails () {

        const nodes = this.d3.selectAll('.node');
        const me = this;

        const nodesDetails = [];

        nodes.each(function(d, i) {

            const node = me.d3.select(this);
            const rect = node.select('rect');
            const text = node.select('text');

            const rectX = d.x;
            const rectY = d.y;

            const textAnchor = text.attr('text-anchor');

            nodesDetails.push({
                name: d.name,
                x: rectX,
                y: rectY,
                textAnchor: textAnchor
            });

        });

        return nodesDetails;
    }

    getLabelsDetails () {

        const icons = this.d3.selectAll('.category-icon');
        const me = this;

        const labelsDetails = [];

        icons.each(function(d, i) {

            const icon = me.d3.select(this);

            const categoryName = icon.attr('data-category');
            const iconX = parseFloat(icon.attr('x'));
            const iconY = parseFloat(icon.attr('y'));

            labelsDetails.push({
                categoryName: categoryName,
                x: iconX,
                y: iconY
            });

        });

        return labelsDetails;
    }

    changeTextDirection(texts){
        const me = this;
        texts.each(function(d){
            const text = me.d3.select(this);
            const currentAnchor = text.attr('text-anchor');

            if(currentAnchor==='end'){
                text
                    .attr('x', SankeyUtil.TEXT_PADDING + me.sankey.nodeWidth())
                    .attr("text-anchor", "start")
                    .selectAll("tspan")
                        .attr("x", SankeyUtil.TEXT_PADDING + me.sankey.nodeWidth());
            }else{
                text
                    .attr('x',  -SankeyUtil.TEXT_PADDING)
                    .attr("text-anchor", "end")
                    .selectAll("tspan")
                        .attr("x",  -SankeyUtil.TEXT_PADDING);
            }
        });

    }

    relocateNodes(link, canvasWidth){
        for(const d of this.sankey.nodes()){
            // relocate nodes
            const nodeId = d.id;
            const htmlNode = document.getElementById(nodeId);
            d.x = d.newX;
            d.y = d.newY;
            this.d3.select(htmlNode).attr('transform', `translate(${d.newX},${d.newY})`);
        }
        
        this.sankey.relayout();
        link.attr("d", this.sankey.linkGenerator());

        // relocate node texts
        const nodes = this.d3.selectAll('.node');
        this.relocateTextsOfNodes(nodes, canvasWidth);
    }

    grayAllLinks(){
        this.d3.selectAll('.link')
            .style("stroke", STYLES.GRAYSCALE.PATH_COLOR)
            .style("stroke-opacity", STYLES.GRAYSCALE.PATH_OPACITY);
    }

    grayAllNodes(){
        const nodeIds = this.d3.selectAll('.node').data().map(n=>n.id)
        for(const nId of nodeIds){
            this.updateNode({
                id: nId,
                nodeColor: STYLES.GRAYSCALE.FILL_COLOR,
                fontColor: STYLES.GRAYSCALE.FONT_COLOR,
                fontSize: STYLES.GRAYSCALE.FONT_SIZE,
                fontWeight: STYLES.GRAYSCALE.FONT_WEIGHT
            });
        } 
    }

    doFiltering(filters, links, nodes){

         // Handle links
        if(!links){
            links = this.d3.selectAll('.link');
        }
        if(!nodes){
            nodes = this.d3.selectAll('.node');
        }

        let filteredLinks = links; // start with all nodes and links
        let filteredNodes = nodes;

        if(filters){
            // filter by graph type
            if(filters[Filters.NAMES.GRAPH_TYPE]){
                const graphType = filters[Filters.NAMES.GRAPH_TYPE];
                [filteredLinks, filteredNodes] = Filters.filterByGraphType(graphType, filteredLinks, filteredNodes);
            }
            // filter by sector
            if(filters[Filters.NAMES.SECTOR]){
                const sector = filters[Filters.NAMES.SECTOR];
                [filteredLinks, filteredNodes] = Filters.filterBySector(sector, filteredLinks, filteredNodes);
            }
        }

        // hide all
        links.style("display", 'none');
        nodes.style("display", 'none');
        // show filtered
        filteredLinks.style('display', '');
        filteredNodes.style('display', '');

    }

    getBoundBox(selection, nodeWidth){
        const data = selection.data();
        const minX = this.d3.min(data, d=>d.x);
        const minY = this.d3.min(data, d=>d.y);

        const width = this.d3.max(data, d=>d.x + nodeWidth) - minX;
        const heigh = this.d3.max(data, d=>d.y + d.dy) - minY;

        return {
            x: minX,
            y: minY,
            width,
            heigh
        };
    }

    setMoreDetails(definitions){
        const element = document.getElementById("more-details-content");
        element.innerHTML = definitions;
    }

    setSelectedElement(element){
        console.log('selected element', element? element.node(): element);
        this.restoreStylesOnAllElements();

        if(!element){
            this.selectedElement = null;
            return;
        }

        const DARKEN_FACTOR = 0.3;
        const elementType = this.typeOfElement(element);

        if(element){

            const elementData = element.data()[0];

            if(elementType==='link'){
                this.updateLink({
                    sourceNodeId: elementData.source.id,
                    targetNodeId: elementData.target.id,
                    pathColor: this.darkenColor(STYLES.HIGHLIGHT.PATH_COLOR, DARKEN_FACTOR),
                    pathOpacity: STYLES.HIGHLIGHT.PATH_OPACITY
                });
            }else if(elementType==='node-rect'){
                this.updateNode({
                    id: elementData.id,
                    nodeColor: this.darkenColor(STYLES.HIGHLIGHT.FILL_COLOR, DARKEN_FACTOR),
                    fontColor: STYLES.HIGHLIGHT.FONT_COLOR,
                    fontSize: STYLES.HIGHLIGHT.FONT_SIZE,
                    fontWeight: STYLES.HIGHLIGHT.FONT_WEIGHT
                });
            }
            else if(elementType==='category-label'){

            }

            this.selectedElement = element;
        }
    }

    typeOfElement(element){
        if(!element){
            return null;
        }
        if(element.node().classList.contains('link') === true){
            return 'link';
        }
        if(element.node().classList.contains('node') === true){
            return 'node';
        }
        if(element.node().classList.contains('node-rect') === true){
            return 'node-rect';
        }
        if(element.node().classList.contains('node-text') === true){
            return 'node-text';
        }
        if(element.node().classList.contains('category-label') === true){
            return 'category-label';
        }
        return null;
    }

    darkenColor = (color, factor = 0.3) => {
        return this.d3.interpolateRgb(color, '#000000')(factor);
    };

    areSelectionsEqual(selection1, selection2){
        if(!selection1 && !selection2){
            return true;
        }
        if((!selection1 && selection2) || (selection1 && !selection2)){
            return false;
        }
        return selection1.node() === selection2.node();
    }

    restoreStylesOnAllElements () {

        this.svg.selectAll('.link')
            .style("stroke", d=>d.color)
            .style("stroke-opacity", null);
        this.svg.selectAll(".node rect")
            .style("fill", d=>d.color);
        this.svg.selectAll(".node text")
            .attr("fill", STYLES.NORMAL.FONT_COLOR)
            .style("font-size", STYLES.NORMAL.FONT_SIZE)
            .style("'font-weight'", STYLES.NORMAL.FONT_WEIGHT);
    }
}